import React, { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import LocationAutocomplete from "@/components/LocationAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { key: "Audience", label: "Fan", blurb: "I want to catch a performance." },
  {
    key: "Community Leader",
    label: "Community Leader",
    blurb: "I might book Dan for my community.",
  },
];

/**
 * Two-step audience capture.
 *   Step 1 — first name + email. Saved immediately (SEGMENT: Audience), so
 *            anyone who bails still lands on the list as a fan.
 *   Step 2 — location + fan/community-leader. Re-POSTs with the location and
 *            the chosen segment; Systeme upserts the same contact.
 */
export default function AudienceDialog({
  trigger,
  testIdPrefix = "audience-dialog",
  source,
  program = "Compose Yourself",
  eyebrow = "Stay in the loop",
  title = "Stay in Dan's orbit.",
  description,
  submitLabel = "Keep me in the loop →",
  doneTitle = "Welcome to Dan's orbit.",
  doneDescription,
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [place, setPlace] = useState(null);
  const [role, setRole] = useState("Audience");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const post = (extra) =>
    axios.post(`${API}/audience`, {
      first_name: firstName.trim(),
      email: email.trim().toLowerCase(),
      program,
      source: source || testIdPrefix,
      segment: "Audience",
      ...extra,
    });

  const submitStep1 = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Need a first name and an email to keep you in the loop.");
      return;
    }
    setSubmitting(true);
    try {
      await post({});
      setStep(2);
    } catch (err) {
      toast.error("Hmm, that didn't go through. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    if (!place) {
      toast.error("Pick your city from the suggestions so we know when we're near you.");
      return;
    }
    setSubmitting(true);
    try {
      await post({
        location: place.display,
        city: place.city,
        state: place.state,
        country: place.country,
        country_code: place.country_code,
        segment: role,
      });
      setDone(true);
      toast.success("You're in. Talk soon.");
    } catch (err) {
      toast.error("Hmm, that didn't go through. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const defaultDescription = (
    <>
      New music, the occasional interesting thought, and a heads-up when
      Dan&apos;s playing in your area. Sent only when there&apos;s something
      actually worth opening.
    </>
  );

  const goldButton = {
    backgroundColor: "transparent",
    color: "#F2D589",
    border: "1px solid #F2D589",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    fontWeight: 600,
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="border-white/15 max-w-lg"
        style={{ backgroundColor: "#0C0826", color: "#F5EFE3" }}
        data-testid={`${testIdPrefix}-content`}
      >
        {done ? (
          <DialogHeader data-testid={`${testIdPrefix}-done`}>
            <p className="text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "#F2D589" }}>
              You&apos;re in
            </p>
            <DialogTitle
              className="text-3xl sm:text-4xl leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              {doneTitle}
            </DialogTitle>
            <DialogDescription className="text-sm sm:text-base mt-2" style={{ color: "rgba(245,239,227,0.75)" }}>
              {doneDescription || (
                <>
                  We&apos;ll write when there&apos;s new music, and when
                  Dan&apos;s heading anywhere near {place?.city || "you"}.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
        ) : step === 1 ? (
          <>
            <DialogHeader>
              <p className="text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "#F2D589" }}>
                {eyebrow}
              </p>
              <DialogTitle
                className="text-2xl sm:text-3xl leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                {title}
              </DialogTitle>
              <DialogDescription className="text-sm sm:text-base mt-2" style={{ color: "rgba(245,239,227,0.78)" }}>
                {description || defaultDescription}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitStep1} className="flex flex-col gap-3 mt-2" data-testid={`${testIdPrefix}-form`}>
              <Input
                type="text"
                required
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid={`${testIdPrefix}-firstname`}
                autoComplete="given-name"
                className="h-11 bg-white/[0.06] border-white/15 text-white placeholder:text-white/40"
              />
              <Input
                type="email"
                required
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={`${testIdPrefix}-email`}
                autoComplete="email"
                className="h-11 bg-white/[0.06] border-white/15 text-white placeholder:text-white/40"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid={`${testIdPrefix}-submit`}
                className="h-12 rounded-full mt-1"
                style={goldButton}
              >
                {submitting ? "Sending…" : submitLabel}
              </button>
              <p className="text-[11px] text-center" style={{ color: "rgba(245,239,227,0.5)" }}>
                No spam, ever.
              </p>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <p className="text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "#F2D589" }}>
                One more thing
              </p>
              <DialogTitle
                className="text-2xl sm:text-3xl leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                Where are you?
              </DialogTitle>
              <DialogDescription className="text-sm sm:text-base mt-2" style={{ color: "rgba(245,239,227,0.78)" }}>
                Have Dan let you know when he&apos;s touring in your area so you
                can catch a performance — or schedule your own!
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitStep2} className="flex flex-col gap-4 mt-2" data-testid={`${testIdPrefix}-form-step2`}>
              <LocationAutocomplete
                value={place}
                onSelect={setPlace}
                testIdPrefix={`${testIdPrefix}-location`}
                placeholder="City / state / country"
              />

              <div className="grid grid-cols-2 gap-3" data-testid={`${testIdPrefix}-role-group`}>
                {ROLES.map((r) => {
                  const active = role === r.key;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setRole(r.key)}
                      data-testid={`${testIdPrefix}-role-${r.key === "Audience" ? "fan" : "leader"}`}
                      className="rounded-xl px-4 py-3 text-left transition-colors"
                      style={{
                        border: `1px solid ${active ? "#F2D589" : "rgba(255,255,255,0.15)"}`,
                        backgroundColor: active ? "rgba(242,213,137,0.1)" : "transparent",
                      }}
                    >
                      <span
                        className="block text-sm"
                        style={{ color: active ? "#F2D589" : "#F5EFE3", fontWeight: 600 }}
                      >
                        {r.label}
                      </span>
                      <span className="block text-[11px] mt-1" style={{ color: "rgba(245,239,227,0.55)" }}>
                        {r.blurb}
                      </span>
                    </button>
                  );
                })}
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid={`${testIdPrefix}-submit-step2`}
                className="h-12 rounded-full"
                style={goldButton}
              >
                {submitting ? "Sending…" : "Done →"}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid={`${testIdPrefix}-skip`}
                className="text-[11px] underline underline-offset-4 mx-auto"
                style={{ color: "rgba(245,239,227,0.5)" }}
              >
                Skip for now — you&apos;re already on the list
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
